//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Button, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'

import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { dynamicSetIndicativeInterestRateDirect } from '@react/signedTxs/dynamicSetIndicativeInterestRate'

import { exchangeRateDecimalStringToExchangeRate, exchangeRateToDecimalString } from '@shared/conversions'
import { DefiExchangeRateCTO } from '@shared/defiCTO'
import { logOT } from '@shared/loggingShared'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { dynamicExchangeRateSetDirect } from '@react/signedTxs/dynamicSetDynamicExchangeRate'

enum DefiExchangeRateCalculationMethod {
  Undefined = 'Undefined',
  Assets = 'Assets',
  Calibration = 'Calibration',
  Native = 'Native',
}

export default function DefiSetExchangeRateForm(props: {
  poolAddr: EthAddr
  defiExchangeRateCTO: DefiExchangeRateCTO
}): ReactElement {
  const { poolAddr, defiExchangeRateCTO } = props
  const {
    webSocket,
    chainConfiguration: { chainConfigurationName },
    userConnection: { signer: poolAdminSigner, roleEnum },
  } = useOtChainState()
  const [indicativeInterestRate, setIndicativeInterestRate] = React.useState<string>(
    Number(defiExchangeRateCTO.interestRate * 100).toFixed(2)
  )
  const [exchangeRate, setExchangeRate] = React.useState<string>(
    props.defiExchangeRateCTO.calibratedExchangeRate.toString()
  )
  const [exchangeRateDecimalString, setExchangeRateDecimalString] = React.useState<string>(
    exchangeRateToDecimalString(props.defiExchangeRateCTO.calibratedExchangeRate.toString())
  )
  const [exchangeRateCalculationMethod, setExchangeRateCalculationMethod] =
    React.useState<DefiExchangeRateCalculationMethod>(DefiExchangeRateCalculationMethod.Calibration)

  const dispatch = useOtDispatch()

  const [errorAmount, _setErrorAmount] = React.useState<string | undefined>(undefined)

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)

  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const setExchangerateAction = (): void => {
    const asyncAction = async (): Promise<void> => {
      setIsSubmitted(true)
      if (poolAdminSigner && roleEnum) {
        await dynamicExchangeRateSetDirect({
          webSocket,
          poolAdminSigner,
          poolAddr,
          exchangeRate,
          chainConfigurationName,
          dispatch,
        })
        await dynamicSetIndicativeInterestRateDirect({
          poolAddr,
          indicativeInterestRate: Math.floor(Number(indicativeInterestRate) * 100).toString(),
          collateralRate: poolCTO.collateralRate.toString(),
          webSocket,
          signer: poolAdminSigner,
          chainConfigurationName,
          roleEnum,
          dispatch,
        })
      }

      const event = new Event('modalClose')
      window.dispatchEvent(event)
    }
    asyncAction().catch((error) => {
      logOT.error('setExchangerateAction', error)
      setIsSubmitted(false)
    })
  }
  const disableButton = false

  return (
    <Grid container sx={{ marginTop: 2 }}>
      <Grid item xs={12}>
        <InputLabel htmlFor="interestRate">Interest Rate</InputLabel>
        <TextField
          id="interestRate"
          inputProps={{
            'data-testid': 'interestRate',
          }}
          InputProps={{
            endAdornment: '%',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Interest Rate (Decimal Form)"
          value={indicativeInterestRate}
          type={'number'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setIndicativeInterestRate(event.target.value)
          }}
        />

        <InputLabel htmlFor="exchangeRateCalculationMethod">Exchange Rate Calculation Method</InputLabel>
        <Select
          id="exchangeRateCalculationMethod"
          value={exchangeRateCalculationMethod}
          onChange={(event: SelectChangeEvent): void => {
            const _exchangeRateCalculationMethod = event.target.value as DefiExchangeRateCalculationMethod
            setExchangeRateCalculationMethod(_exchangeRateCalculationMethod)
            if (_exchangeRateCalculationMethod === DefiExchangeRateCalculationMethod.Calibration) {
              setExchangeRateDecimalString(
                exchangeRateToDecimalString(defiExchangeRateCTO.calibratedExchangeRate.toString())
              )
              setExchangeRate(defiExchangeRateCTO.calibratedExchangeRate.toString())
            } else if (_exchangeRateCalculationMethod === DefiExchangeRateCalculationMethod.Assets) {
              setExchangeRateDecimalString(
                exchangeRateToDecimalString(defiExchangeRateCTO.assetBasedExchangeRate.toString())
              )
              setExchangeRate(defiExchangeRateCTO.assetBasedExchangeRate.toString())
            } else if (_exchangeRateCalculationMethod === DefiExchangeRateCalculationMethod.Native) {
              setExchangeRateDecimalString(
                exchangeRateToDecimalString(defiExchangeRateCTO.nativeDefiExchangeRate.toString())
              )
              setExchangeRate(defiExchangeRateCTO.nativeDefiExchangeRate.toString())
            }
          }}
        >
          <MenuItem value={DefiExchangeRateCalculationMethod.Calibration}>Calibration</MenuItem>
          <MenuItem value={DefiExchangeRateCalculationMethod.Native}>Native</MenuItem>
          {BigInt(defiExchangeRateCTO.totalPoolAssets) > 0n && (
            <MenuItem value={DefiExchangeRateCalculationMethod.Assets}>Assets</MenuItem>
          )}
        </Select>
      </Grid>
      <Grid item xs={12}>
        <InputLabel htmlFor="exchangeRate">ExchangeRate</InputLabel>
        <TextField
          id="exchangeRate"
          inputProps={{
            'data-testid': 'exchangeRate',
          }}
          sx={{ width: '300px' }}
          variant="outlined"
          placeholder="Enter Exchange Rate (Decimal Form)"
          value={exchangeRateDecimalString}
          type={'number'}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            setExchangeRateDecimalString(event.target.value)
            setExchangeRate(exchangeRateDecimalStringToExchangeRate(event.target.value).toString())
          }}
          error={!!errorAmount}
          helperText={errorAmount}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Button
          disabled={isSubmitted || disableButton}
          sx={sx}
          onClick={() => {
            setExchangerateAction()
          }}
          variant={'contained'}
        >
          <Typography variant={'buttonBold'}>Set Exchange rate</Typography>
        </Button>
      </Grid>
      {disableButton && (
        <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
          Disabled
        </Typography>
      )}
    </Grid>
  )
}
